import { HTMLProps, useMemo, useState } from 'react';

import { Button, Collapse } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChevronUp, faChevronDown } from '@fortawesome/pro-light-svg-icons';

import Placeholder from '@components/Placeholder/Placeholder';
import useBreakpoint, { SIZE_XS, SIZE_SM } from '@hooks/useBreakpoint';
import { IS_NATERA } from '@constants/constants';
import './ExpandableSection.scss';

interface IProps extends HTMLProps<HTMLElement> {
  children: React.ReactNode;
  headerTitle: React.ReactNode;
  defaultIsCollapsed?: boolean;
  expandableContentId: string;
  loading?: boolean;
}

const ExpandableSection = ({
  children,
  headerTitle,
  defaultIsCollapsed = false,
  expandableContentId,
  loading = false,
  className,
  ...props
}: IProps) => {
  const [open, setOpen] = useState<boolean>(!defaultIsCollapsed);

  const breakpoint = useBreakpoint();
  const isWidthSMdown = useMemo(() => breakpoint === SIZE_XS || breakpoint === SIZE_SM, [breakpoint]);
  const buttonSize = isWidthSMdown ? 'sm' : undefined;
  const iconSize = isWidthSMdown ? 16 : 20;
  const headerClass = IS_NATERA ? '' : 'fw-bold';

  return (
    <section className={['box-shadow-50 bg-white expandable-section', className].filter(Boolean).join(' ')} {...props}>
      <div className='d-flex align-items-center section-header'>
        {loading ? (
          <Placeholder size='2xl' className='w-100' />
        ) : (
          <>
            <Button
              variant='ghost'
              size={buttonSize}
              aria-label={open ? 'Close section' : 'Open section'}
              aria-expanded={open}
              aria-controls={expandableContentId}
              onClick={() => setOpen(open => !open)}
            >
              <FontAwesomeIcon icon={(open ? faChevronUp : faChevronDown) as IconDefinition} fontSize={iconSize} />
            </Button>
            <h2 className={headerClass + ' mb-0 section-header-title'}>{headerTitle}</h2>
          </>
        )}
      </div>
      <Collapse in={open} timeout={0}>
        <div id={expandableContentId} className='d-flex flex-column gap-3 gap-lg-4 expandable-content'>
          {children}
        </div>
      </Collapse>
    </section>
  );
};

export default ExpandableSection;
